import { connect, Dispatch } from 'react-redux';

import { AppState } from 'src';
import { history } from 'src';

import { ModalProps } from '@common/components';
import { toggleModal } from '@common/components/common.actions';
import { RouteDefinitions, getRoute } from '@common/route-definitions';
import { ContractViewModel, EmailNotificationViewModel } from '@models/Contract';
import { PaymentViewModel } from '@models/Payment';

import { savePaymentInfo, saveContract, cloneContract } from '../../../contract.actions';
import { ContractPaymentConfirmation } from './confirmation';
import { ContractPaymentComponent, ContractPaymentComponentProperties } from './contract-payment.component';
import { getAffiliatedAccounts, getSavedCreditCardsForContract } from '../contract-payment.actions';
import { getContractEmailList } from '../../contract-email/contract-email.actions';
import { ContractEmail } from 'src/contract';

export interface ContractPaymentProperties {
  readonly isRenewal: boolean;
  readonly buttonRef: (button: HTMLButtonElement) => void;
  readonly hideStepLabel?: boolean;
  readonly contractId?: number;
}

// This is used to translate the state of the page to the props on the component
function mapStateToProps(
  state: AppState,
  ownProps: ContractPaymentProperties
): Partial<ContractPaymentComponentProperties> {
  return {
    affiliatedAccounts: state.contract.payment.affiliatedAccounts,
    contractEndDate: state.contract.contractDetailsVM ? state.contract.contractDetailsVM!.termEnd : ({} as Date),
    contractId: state.contract.id || ownProps.contractId,
    contractVM: state.contract.contractVM ? state.contract.contractVM : ({} as ContractViewModel),
    isRenewal: ownProps.isRenewal,
    savedCreditCards: state.contract.payment.savedCreditCards,
    sentEmails: state.contract.sentEmails,
    totalPrice: state.contract.totalPrice,
    propertySalesTypes: state.contract.propertySaleTypes,
    orderType: state.contract.orderType,
    isNextDisabled: state.contract.isNextDisabled,
    hostedPayment: true,
    hideStepLabel: ownProps.hideStepLabel,
    contractBalance: state.contract.paymentEdit.contractPaymentDetail
      ? state.contract.paymentEdit.contractPaymentDetail.paymentBalance
      : 0,
    contractEmailList: state.contract.contractEmail.contractEmailList
  };
}

// This should create proxy wrappers for actions used by the component
function mapDispatchToProps(
  dispatch: Dispatch<AppState>,
  ownProps: ContractPaymentProperties
): Partial<ContractPaymentComponentProperties> {
  return {
    initialize: () => {
      if (ownProps.contractId) {
        return dispatch(cloneContract(ownProps.contractId, true)).then(() => {
          return Promise.all([dispatch(getAffiliatedAccounts()), dispatch(getSavedCreditCardsForContract())]);
        });
      }
      return Promise.all([dispatch(getAffiliatedAccounts()), dispatch(getSavedCreditCardsForContract())]).then(() => {
        return;
      });
    },
    saveContract: (contractVM: ContractViewModel, callback: () => void) => {
      dispatch(saveContract(contractVM, callback));
    },
    submit: (
      isRenewal: boolean,
      contractId: number,
      payment: PaymentViewModel,
      demandLetter?: boolean,
      contractBalance?: number,
      emailList?: Array<EmailNotificationViewModel>
    ) => {
      dispatch(
        savePaymentInfo(contractId, payment, demandLetter, () => {
          if (contractBalance && contractBalance > 0) {
            history.push(getRoute(RouteDefinitions.contract.Details, { id: +contractId }));
          } else {
            let creditCardLastFour: string | undefined = undefined;
            if (
              payment.paymentCreditCardInitial &&
              payment.paymentCreditCardInitial.number &&
              payment.paymentCreditCardInitial.number.length >= 4
            ) {
              const ccNumber: string = payment.paymentCreditCardInitial.number;
              const length: number = Math.min(4, ccNumber.length);
              creditCardLastFour = ccNumber.substr(ccNumber.length - length, length);
            }
            const callback = () => {
              dispatch(
                toggleModal({
                  title: isRenewal ? 'Contract renewal completed.' : 'The New Contract has been completed!',
                  component: ContractPaymentConfirmation,
                  childProps: {
                    contractId: contractId,
                    creditCardLastFour: creditCardLastFour,
                    paymentDate: payment.paymentDate
                  },
                  closeLabel: 'Close',
                  closeCallback: () => history.push('/'),
                  hideX: true,
                  saveLabel: 'View Contract Details',
                  saveCallback: () => {
                    dispatch(toggleModal());
                    history.push(getRoute(RouteDefinitions.contract.Details, { id: contractId }));
                  }
                })
              );
            };
            if (emailList && emailList.length > 0) {
              dispatch(
                toggleModal({
                  title: 'Select Contract Notifications',
                  component: ContractEmail,
                  childProps: {
                    contractId: contractId,
                    saveCallback: callback
                  },
                  closeLabel: 'Close',
                  hideX: true,
                  saveLabel: 'Continue',
                  closeCallback: callback,
                  saveCallback: callback
                })
              );
            } else {
              dispatch(callback);
            }
          }
        })
      );
    },
    toggleModal: (modalProps: ModalProps) => {
      dispatch(toggleModal(modalProps));
    },
    getContractEmailList: async (contractId: number) => {
      await dispatch(getContractEmailList(contractId));
      return Promise.resolve();
    }
  };
}

// Connect function "wires" both of the map functions and returns a wrapper container for the component
export const ContractHostedPayment = connect(
  mapStateToProps,
  mapDispatchToProps
)(ContractPaymentComponent);



// WEBPACK FOOTER //
// ./src/contract/_components/contract-payment/_components/contract-payment-hosted-container.ts